import { Box, Table as MuiTable } from '@mui/material'
import styled from '@emotion/styled/macro'
import theme from 'theme'

export const BenchmarkCardContainer = styled(Box)<{ isexpanded?: string }>`
  border-bottom: 1px solid rgba(147, 143, 153, 0.16);
  cursor: pointer;
  border-radius: ${theme.attributes.borderRadius.normal};
  background: ${theme.colors.backgroundMedium};
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};
  padding: ${theme.spacing.xsm}px ${theme.spacing.small}px;

  &:hover {
    background: ${theme.colors.legacyPrimaryLight3};
  }

  .category-name {
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.large};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 20px;
    letter-spacing: 0.25px;
    text-decoration: none;

    &:hover {
      color: ${theme.colors.cta};
      text-decoration-line: underline;
    }
  }

  .card-button {
    padding-left: 0;
    font-size: ${theme.typography.fontSize.large};
    line-height: 16px;
    font-weight: ${theme.typography.fontWeight.bold};
    letter-spacing: 0.5px;
    margin-top: ${theme.spacing.xSmall}px;

    svg {
      transition: transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
      transform: ${({ isexpanded }) => (isexpanded === 'true' ? 'rotate(45deg)' : 'rotate(0deg)')};
    }

    &:hover,
    &:focus-within {
      background: transparent;
      color: ${theme.colors.foreground};

      svg {
        path {
          fill: ${theme.colors.foreground};
        }
      }
    }
  }

  .no-category {
    border-radius: 2px;
    background: ${theme.colors.legacyBorderLow3};
    box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 16px;
    letter-spacing: 0.4px;
    color: ${theme.colors.foreground};
    padding: 6px 7px;
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xSmall}px;
    margin-top: ${theme.spacing.small}px;
  }

  html[data-theme='light'] & {
    border-radius: 12px;
    border: 1px solid ${theme.colors.strokePrimary};
    background: ${theme.colors.backgroundDarker};
    box-shadow: none;

    &:hover {
      background: ${theme.colors.hoverPrimary};
    }

    .no-category img {
      filter: brightness(0) opacity(0.5);
    }
  }
`

export const BenchmarkTable = styled(MuiTable)`
  margin-top: ${theme.spacing.xSmall}px;

  .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root {
    color: ${theme.colors.foreground} !important;
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-weight: ${theme.typography.fontWeight.bold};
    line-height: 16px;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-bottom: none;
    padding-bottom: ${theme.spacing.xSmall}px;
    text-align: center;

    &:first-of-type {
      text-align: left;
      padding-left: 0;
    }

    &:nth-of-type(2) {
      text-align: left;
    }

    &:last-of-type {
      text-align: right;
      padding-right: 0;
    }
  }

  .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root {
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.xLarge};
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 24px;
    letter-spacing: 0.5px;
    white-space: nowrap;
    border-bottom: none;
    padding-top: ${theme.spacing.xSmall}px;
    padding-bottom: ${theme.spacing.xSmall}px;

    &:first-of-type {
      color: ${theme.colors.cta};
      text-align: left;
      padding-left: 0;
      text-transform: uppercase;
      text-decoration-line: underline;
    }

    &:nth-of-type(2) {
      text-align: left;
    }

    &:last-of-type {
      text-align: right;
      padding-right: 0;
    }

    &.cpi-cell {
      cursor: pointer;
    }
  }

  .MuiTableHead-root .MuiTableRow-root {
    .category-title {
      width: 95px !important;
      white-space: pre-wrap !important;
      color: ${theme.colors.foreground};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.medium};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 16px;
      letter-spacing: 0.4px;
    }

    .category-cell {
      color: ${theme.colors.foreground};
      font-family: ${theme.typography.fontFamily.primary};
      font-size: ${theme.typography.fontSize.xxxLarge};
      font-weight: ${theme.typography.fontWeight.normal};
      line-height: 32px;

      @media (max-width: 1279px) {
        padding: ${theme.spacing.xSmall}px ${theme.spacing.small}px;

        &:nth-of-type(2) {
          text-align: center;
        }

        &:nth-of-type(3) {
          text-align: center;
        }
      }
    }
  }
`

export const CategoryBenchmarkWrapper = styled(Box)`
  border-top: 1px solid ${theme.colors.controlBorder};
  margin-top: ${theme.spacing.xSmall}px;
`

export const BenchmarkValueWrapper = styled(Box)<{
  one: string | number
  two: string | number
  three: string | number
}>`
  width: 100%;
  height: 20px;
  display: flex;
  align-items: center;
  cursor: pointer;
  box-sizing: content-box;
  color: ${theme.colors.white};
  text-align: center;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.xLarge};
  font-weight: ${theme.typography.fontWeight.bold};
  line-height: 24px;
  letter-spacing: 0.1px;

  &:hover {
    .one {
      opacity: 0.38;
    }

    .two {
      opacity: 0.38;
    }

    .three {
      opacity: 0.38;
    }
  }

  .one {
    width: ${({ one }) => `${one}%`};
    border-radius: ${theme.attributes.borderRadius.quaternary} 0 0 ${theme.attributes.borderRadius.quaternary};
    border-top: 1px solid #8e9aff;
    border-bottom: 1px solid #8e9aff;
    border-left: 1px solid #8e9aff;
    background: linear-gradient(180deg, #8e9aff 0%, rgba(142, 154, 255, 0.4) 100%);
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border: 1px solid ${theme.colors.white};
      box-shadow: 0 0 ${theme.attributes.borderRadius.quaternary} 0 rgba(255, 255, 255, 0.25);
      opacity: 1;
    }
  }

  .two {
    width: ${({ two }) => `${two}%`};
    border-top: 1px solid ${theme.baseColors.info[40]};
    border-bottom: 1px solid ${theme.baseColors.info[40]};
    background: linear-gradient(180deg, rgba(240, 149, 67, 0.94) 0%, rgba(240, 149, 67, 0.4) 100%);
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border: 1px solid ${theme.colors.white};
      box-shadow: 0 0 ${theme.attributes.borderRadius.quaternary} 0 rgba(255, 255, 255, 0.25);
      opacity: 1;
    }
  }

  .three {
    width: ${({ three }) => `${three}%`};
    border-radius: 0 16px 16px 0;
    border-top: 1px solid ${theme.baseColors.primary[50]};
    border-right: 1px solid ${theme.baseColors.primary[50]};
    border-bottom: 1px solid ${theme.baseColors.primary[50]};
    background: linear-gradient(180deg, #781a7e 0%, rgba(120, 26, 126, 0.4) 100%);
    color: ${theme.colors.white};
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      border: 1px solid ${theme.colors.white};
      box-shadow: 0 0 ${theme.attributes.borderRadius.quaternary} 0 rgba(255, 255, 255, 0.25);
      opacity: 1;
    }
  }

  html[data-theme='light'] & {
    color: #18181f;

    .two {
      color: #18181f;
    }

    .one,
    .three {
      color: #ffffff;
    }

    .one {
      border-color: #7480e8;
      background: #7480e8;
    }

    .two {
      border-color: #e8a048;
      background: #e8a048;
    }

    .three {
      border-color: #b8399f;
      background: #b8399f;
    }
  }
`

export const NoBenchmarkWrapper = styled(Box)`
  border-radius: 2px;
  background: ${theme.colors.borderLow};
  box-shadow: ${theme.attributes.boxShadow.overallScoreWrapper};
  display: flex;
  align-items: center;
  padding: ${theme.spacing.xSmall}px;

  html[data-theme='light'] & {
    background: ${theme.colors.surfaceItemBackgroundLow};
  }

  .no-benchmark-text {
    margin-left: ${theme.spacing.xSmall}px;
    color: ${theme.colors.foreground};
    font-family: ${theme.typography.fontFamily.primary};
    font-size: ${theme.typography.fontSize.medium};
    font-style: normal;
    font-weight: ${theme.typography.fontWeight.normal};
    line-height: 16px;
    letter-spacing: 0.4px;
  }
`
